import { useMemo } from "react";
import { comboKey, type ModifierState } from "@/domain/profile-resolver/index";
import type { DiffRow } from "./diff";
import { faceCodes } from "./flashlight";

/**
 * The board flashlight, A and B. Off compare, the A board lights the
 * search/hover codes. While comparing, with no Changes row hovered each board lights its OWN changed
 * codes on the face it currently shows (the active combo), so a lit cap always displays the changed
 * binding; an empty set yields null so the board reads normally instead of dimming every cap. Hovering
 * a Changes row narrows each board to exactly that row's positions on its side.
 */
export function useCompareFlashlight({
  diff,
  hoverDiffId,
  mods,
  comparing,
  flashlightCodes,
}: {
  diff: { rows: DiffRow[]; same: number } | null;
  hoverDiffId: string | null;
  mods: ModifierState;
  comparing: boolean;
  flashlightCodes: string[] | null;
}): { litA: string[] | null; litB: string[] | null } {
  const hoverRow = useMemo(
    () => diff?.rows.find((r) => r.id === hoverDiffId) ?? null,
    [diff, hoverDiffId],
  );
  const activeCombo = comboKey(mods);
  const diffFaceA = useMemo(
    () =>
      diff
        ? faceCodes(
            diff.rows.flatMap((r) => r.a),
            activeCombo,
          )
        : null,
    [diff, activeCombo],
  );
  const diffFaceB = useMemo(
    () =>
      diff
        ? faceCodes(
            diff.rows.flatMap((r) => r.b),
            activeCombo,
          )
        : null,
    [diff, activeCombo],
  );
  const litA = comparing
    ? hoverRow
      ? faceCodes(hoverRow.a, activeCombo)
      : diffFaceA
    : flashlightCodes;
  const litB = comparing ? (hoverRow ? faceCodes(hoverRow.b, activeCombo) : diffFaceB) : null;
  return { litA, litB };
}
